'use client';

import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { clsx } from 'clsx';
import { useBuilderStore } from '@/store/builderStore';

const steps = [
  {
    id: 1,
    label: 'Describe Product',
    sub: 'Tell us what you need',
    icon: '✏️',
  },
  {
    id: 2,
    label: 'Material & Finish',
    sub: 'Choose material & finish',
    icon: '🔩',
  },
  {
    id: 3,
    label: 'Dimensions',
    sub: 'Enter product dimensions',
    icon: '📐',
  },
  {
    id: 4,
    label: 'AI Preview',
    sub: 'See your product',
    icon: '🤖',
  },
  {
    id: 5,
    label: 'Review & Submit',
    sub: 'Get quotation',
    icon: '📋',
  },
];

export default function Sidebar() {
  const { currentStep } = useBuilderStore();

  return (
    <aside className="w-72 shrink-0 hidden lg:flex flex-col">
      {/* Logo / Brand */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/30">
            <span className="text-white text-base font-black">✦</span>
          </div>
          <div>
            <p className="text-xs font-bold text-amber-500 uppercase tracking-widest">Export</p>
            <p className="text-sm font-black text-slate-800 leading-tight">Excellence</p>
          </div>
        </div>
      </div>

      {/* Steps */}
      <div className="flex flex-col gap-2">
        {steps.map((step, idx) => {
          const isCompleted = currentStep > step.id;
          const isActive = currentStep === step.id;

          return (
            <motion.div
              key={step.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.08, duration: 0.4, ease: 'easeOut' }}
            >
              <div
                className={clsx(
                  'relative flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all duration-300 cursor-default select-none',
                  {
                    'bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 shadow-sm':
                      isActive,
                    'bg-emerald-50 border border-emerald-100': isCompleted,
                    'bg-transparent hover:bg-slate-50': !isActive && !isCompleted,
                  }
                )}
              >
                {/* Connector line */}
                {idx < steps.length - 1 && (
                  <div
                    className={clsx(
                      'absolute left-[27px] top-full w-0.5 h-2 transition-colors duration-300',
                      {
                        'bg-emerald-300': isCompleted,
                        'bg-amber-200': isActive,
                        'bg-slate-200': !isActive && !isCompleted,
                      }
                    )}
                  />
                )}

                {/* Step indicator */}
                <div
                  className={clsx(
                    'w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 transition-all duration-300',
                    {
                      'bg-gradient-to-br from-amber-400 to-amber-600 text-white shadow-md shadow-amber-500/30':
                        isActive,
                      'bg-emerald-500 text-white shadow-md shadow-emerald-500/30': isCompleted,
                      'bg-slate-100 text-slate-400 border border-slate-200':
                        !isActive && !isCompleted,
                    }
                  )}
                >
                  {isCompleted ? (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                    >
                      <Check size={14} strokeWidth={3} />
                    </motion.div>
                  ) : (
                    <span>{step.id}</span>
                  )}
                </div>

                {/* Step text */}
                <div className="flex-1 min-w-0">
                  <p
                    className={clsx('text-sm font-semibold truncate transition-colors duration-200', {
                      'text-amber-700': isActive,
                      'text-emerald-700': isCompleted,
                      'text-slate-500': !isActive && !isCompleted,
                    })}
                  >
                    {step.label}
                  </p>
                  <p
                    className={clsx('text-xs truncate transition-colors duration-200', {
                      'text-amber-500': isActive,
                      'text-emerald-500': isCompleted,
                      'text-slate-400': !isActive && !isCompleted,
                    })}
                  >
                    {step.sub}
                  </p>
                </div>

                {/* Active pulse */}
                {isActive && (
                  <div className="w-2 h-2 rounded-full bg-amber-500 shrink-0 animate-pulse" />
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Progress */}
      <div className="mt-8 p-4 rounded-xl bg-slate-50 border border-slate-100">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-slate-500">Progress</span>
          <span className="text-xs font-bold text-amber-600">
            {Math.round(((currentStep - 1) / 4) * 100)}%
          </span>
        </div>
        <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full"
            initial={{ width: '0%' }}
            animate={{ width: `${((currentStep - 1) / 4) * 100}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>
        <p className="text-xs text-slate-400 mt-2">
          Step {currentStep} of 5
        </p>
      </div>

      {/* Help box */}
      <div className="mt-4 p-4 rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 text-white">
        <p className="text-xs font-bold text-amber-400 uppercase tracking-wider mb-1">Need Help?</p>
        <p className="text-xs text-slate-300 leading-relaxed">
          Our export specialists are ready to assist you with your product inquiry.
        </p>
        <div className="mt-3 flex items-center gap-2 text-xs text-amber-400 font-medium">
          <span>📞</span>
          <span>+1 (800) 000-0000</span>
        </div>
      </div>
    </aside>
  );
}
